/**
 * Per-User, client-seitiger Session-State — **nichts** wird im Dokument gespeichert.
 *
 * Begründung: die `chatId` referenziert einen Chat im OWUI-Account *dieses* Users.
 * Läge sie im (geteilten) Dokument, würde ein anderer Mitarbeiter beim Öffnen versuchen,
 * einen fremden Chat zu laden. Deshalb lebt die Zuordnung Dokument → Session **nur auf
 * dem Client** (IndexedDB, pro Origin/User) und verlässt die Datei nie.
 *
 * Gekeyt per Dokument-URL: jedes gespeicherte Dokument behält auf diesem Client seine
 * eigene Konversation; ungespeicherte/Scratch-Dokumente teilen sich einen Slot.
 *
 * Der Store ist hinter einem schmalen Interface gekapselt — austauschbar (Test-In-Memory
 * heute, Server-backed später, vgl. „v1 client, später Server").
 */

import type { ApplyMode } from "@lupp/shared";

export interface SessionRecord {
  chatId?: string;
  selectedModelId?: string;
  /** Web search active for THIS document — sticks across chats of the same document. */
  webSearch?: boolean;
  /** Active project (folder) scope for THIS document — restored like chatId. */
  folderId?: string | null;
  /** How accepted edits are written into the document (per document). */
  applyMode?: ApplyMode;
}

export interface SessionStore {
  get(key: string): Promise<SessionRecord | undefined>;
  set(key: string, value: SessionRecord): Promise<void>;
}

export interface PersistedSession {
  chatId?: string;
  selectedModelId?: string;
  webSearch?: boolean;
  folderId?: string | null;
  applyMode?: ApplyMode;
}

const DB_NAME = "lupp";
const STORE_NAME = "sessions";
const UNSAVED_KEY = "__unsaved__";
const LS_DEFAULT_MODEL = "lupp.defaultModel";

// ── IndexedDB-backed store (default) ──────────────────────────────────────────

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = (): void => {
      req.result.createObjectStore(STORE_NAME);
    };
    req.onsuccess = (): void => resolve(req.result);
    req.onerror = (): void => reject(req.error);
  });
}

const indexedDbStore: SessionStore = {
  async get(key) {
    const db = await openDb();
    return new Promise<SessionRecord | undefined>((resolve) => {
      const r = db.transaction(STORE_NAME, "readonly").objectStore(STORE_NAME).get(key);
      r.onsuccess = (): void => resolve(r.result as SessionRecord | undefined);
      r.onerror = (): void => resolve(undefined);
    });
  },
  async set(key, value) {
    const db = await openDb();
    return new Promise<void>((resolve) => {
      const tx = db.transaction(STORE_NAME, "readwrite");
      tx.objectStore(STORE_NAME).put(value, key);
      tx.oncomplete = (): void => resolve();
      tx.onerror = (): void => resolve();
    });
  },
};

// ── Injectable seams (default to IndexedDB / live Office) ─────────────────────

let store: SessionStore = indexedDbStore;
let cachedKey: string | undefined;

/** Resolves the document identity (its url; unsaved/scratch docs share one slot). */
async function documentKey(): Promise<string> {
  if (cachedKey) return cachedKey;
  cachedKey = await new Promise<string>((resolve) => {
    try {
      const doc = (Office as { context?: { document?: { getFilePropertiesAsync?: (cb: (r: { value?: { url?: string } }) => void) => void } } }).context?.document;
      if (doc?.getFilePropertiesAsync) {
        doc.getFilePropertiesAsync((r) => {
          const url = r?.value?.url;
          resolve(url && url.length > 0 ? url : UNSAVED_KEY);
        });
      } else {
        resolve(UNSAVED_KEY);
      }
    } catch {
      resolve(UNSAVED_KEY);
    }
  });
  return cachedKey;
}

// ── Public API ────────────────────────────────────────────────────────────────

/** Liest die client-lokale Session dieses Dokuments (chatId + gewähltes Modell). */
export async function loadPersistedSession(): Promise<PersistedSession> {
  try {
    const rec = await store.get(await documentKey());
    return { chatId: rec?.chatId, selectedModelId: rec?.selectedModelId, webSearch: rec?.webSearch, folderId: rec?.folderId, applyMode: rec?.applyMode };
  } catch {
    return {};
  }
}

/** Persistiert den Websuche-Status pro Dokument (sticky über Chats desselben Dokuments). */
export async function persistWebSearch(enabled: boolean): Promise<void> {
  try {
    const key = await documentKey();
    const rec = (await store.get(key)) ?? {};
    await store.set(key, { ...rec, webSearch: enabled });
  } catch {
    /* best effort */
  }
}

/** Persistiert die chatId dieses Dokuments (`undefined` = löschen). */
export async function persistChatId(chatId: string | undefined): Promise<void> {
  try {
    const key = await documentKey();
    const rec = (await store.get(key)) ?? {};
    await store.set(key, { ...rec, chatId });
  } catch {
    /* best effort */
  }
}

/** Persistiert den aktiven Projekt-Scope dieses Dokuments (`null` = kein Projekt). */
export async function persistFolderId(folderId: string | null): Promise<void> {
  try {
    const key = await documentKey();
    const rec = (await store.get(key)) ?? {};
    await store.set(key, { ...rec, folderId });
  } catch {
    /* best effort */
  }
}

/** Persistiert den Apply-Modus dieses Dokuments (wie Änderungen geschrieben werden). */
export async function persistApplyMode(applyMode: ApplyMode): Promise<void> {
  try {
    const key = await documentKey();
    const rec = (await store.get(key)) ?? {};
    await store.set(key, { ...rec, applyMode });
  } catch {
    /* best effort */
  }
}

/** Persistiert das gewählte Modell pro Dokument (IndexedDB) UND als globalen Default (localStorage). */
export async function persistSelectedModel(modelId: string): Promise<void> {
  try {
    const key = await documentKey();
    const rec = (await store.get(key)) ?? {};
    await store.set(key, { ...rec, selectedModelId: modelId });
  } catch {
    /* best effort */
  }
  try {
    window.localStorage.setItem(LS_DEFAULT_MODEL, modelId);
  } catch {
    /* localStorage nicht verfügbar */
  }
}

/** Globales Default-Modell (dokumentübergreifend) zum Vorbelegen eines frischen Dokuments. */
export function getGlobalDefaultModel(): string | undefined {
  try {
    return window.localStorage.getItem(LS_DEFAULT_MODEL) ?? undefined;
  } catch {
    return undefined;
  }
}

// ── Test seams ────────────────────────────────────────────────────────────────

export function __setSessionStoreForTests(s: SessionStore): void {
  store = s;
}
export function __setDocumentKeyForTests(key: string | undefined): void {
  cachedKey = key;
}
